import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, Checkbox, IconButton, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { useEscapeKey } from "hooks";
import { IApiServiceError } from "store";
import { SlicePromptInvestor, SlicePromptResult } from "store/types";

import {
  COLOR_BG_HOVER_SUBTLE,
  COLOR_BG_INFO,
  COLOR_DISABLED,
  COLOR_ERROR,
  COLOR_INFO,
  COLOR_PRIMARY,
  COLOR_SECONDARY,
  COLOR_STROKE_DIVIDER,
  COLOR_STROKE_IDLE
} from "../colors";

const NAME_MAX_LENGTH = 100;

type PromptMode = "create" | "edit";

interface Props {
  mode?: PromptMode;
  initialSlice?: { name: string; memberIds: SlicePromptInvestor["id"][] };
  investors: SlicePromptInvestor[];
  loadError?: boolean;
  onRetryLoad?: () => void;
  saveSlice: (data: SlicePromptResult) => Promise<void>;
  onSubmit: (result: SlicePromptResult) => void;
  onCancel: () => void;
}

type Step = 1 | 2;

export const CreateSlicePrompt: React.FC<Props> = ({
  mode = "create",
  initialSlice,
  investors,
  loadError,
  onRetryLoad,
  saveSlice,
  onSubmit,
  onCancel
}) => {
  const [step, setStep] = useState<Step>(1);
  const [name, setName] = useState(initialSlice?.name ?? "");
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<SlicePromptInvestor["id"]>>(
    new Set(initialSlice?.memberIds ?? [])
  );

  const listRef = useRef<HTMLDivElement>(null);
  const savedScrollTop = useRef(0);
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  const isEdit = mode === "edit";
  const trimmedName = name.trim();
  const isNameValid = trimmedName.length > 0;

  const selectedInvestors = useMemo(() => investors.filter((i) => selectedIds.has(i.id)), [investors, selectedIds]);

  const filteredList = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q) return investors.filter((i) => i.name.toLowerCase().includes(q));
    return [...investors.filter((i) => selectedIds.has(i.id)), ...investors.filter((i) => !selectedIds.has(i.id))];
  }, [investors, selectedIds, search]);

  const toggleInvestor = (investor: SlicePromptInvestor) => {
    savedScrollTop.current = listRef.current?.scrollTop ?? 0;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(investor.id)) {
        next.delete(investor.id);
      } else {
        next.add(investor.id);
      }
      return next;
    });
  };

  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = savedScrollTop.current;
  }, [selectedIds]);

  useEscapeKey(onCancel);

  const handleNameChange = (next: string) => {
    setName(next);
    if (nameError) setNameError(null);
  };

  const handleNext = () => {
    if (!isNameValid) return;
    setStep(2);
  };

  const handleBack = () => {
    setApiError(null);
    setStep(1);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setApiError(null);
    setNameError(null);
    try {
      const result: SlicePromptResult = {
        name: trimmedName,
        memberIds: Array.from(selectedIds)
      };
      await saveSlice(result);
      onSubmit(result);
    } catch (e) {
      if ((e as IApiServiceError).errors?.name) {
        setNameError((e as IApiServiceError).errors.name[0]);
        setStep(1);
      } else {
        const message = e instanceof Error && e.message ? e.message : "Slice could not be saved. Try again.";
        setApiError(message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Stack
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        outline: "none",
        width: 640,
        maxHeight: "calc(100vh - 64px)",
        bgcolor: "white",
        borderRadius: "8px",
        boxShadow:
          "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)",
        overflow: "hidden"
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: "24px", py: "16px", borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}` }}
      >
        <Stack>
          <Typography sx={{ color: COLOR_PRIMARY, fontSize: "18px", lineHeight: "24px", fontWeight: 500 }}>
            {isEdit ? "Edit Slice" : "Create Slice"}
          </Typography>
          <Typography sx={{ color: COLOR_SECONDARY, fontSize: "13px", lineHeight: "16px", mt: "2px" }}>
            Step {step} of 2 — {step === 1 ? "Name & Members" : "Preview"}
          </Typography>
        </Stack>
        <IconButton
          onClick={onCancel}
          sx={{
            padding: "4px",
            color: COLOR_PRIMARY,
            "&:hover": { backgroundColor: COLOR_BG_HOVER_SUBTLE }
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      <Stack sx={{ p: "20px 24px", gap: "16px", flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
        {step === 1 ? (
          <Step1
            name={name}
            setName={handleNameChange}
            nameError={nameError}
            search={search}
            setSearch={setSearch}
            investors={filteredList}
            selectedIds={selectedIds}
            toggleInvestor={toggleInvestor}
            loadError={loadError}
            onRetryLoad={onRetryLoad}
            selectedCount={selectedIds.size}
            listRef={listRef}
          />
        ) : (
          <Step2 name={trimmedName} selectedInvestors={selectedInvestors} />
        )}
      </Stack>

      <Stack sx={{ px: "24px", py: "16px", borderTop: `1px solid ${COLOR_STROKE_DIVIDER}`, gap: "8px" }}>
        {apiError && (
          <Typography sx={{ color: COLOR_ERROR, fontSize: "14px", lineHeight: "20px" }}>{apiError}</Typography>
        )}
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap="12px">
          {step === 2 ? (
            <Button
              variant="text"
              onClick={handleBack}
              sx={{
                textTransform: "none",
                color: COLOR_PRIMARY,
                "&:hover": { backgroundColor: COLOR_BG_HOVER_SUBTLE }
              }}
            >
              Back
            </Button>
          ) : (
            <Box />
          )}
          <Stack direction="row" gap="12px">
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                textTransform: "none",
                color: COLOR_PRIMARY,
                borderColor: COLOR_STROKE_IDLE,
                "&:hover": { borderColor: COLOR_PRIMARY, backgroundColor: COLOR_BG_HOVER_SUBTLE }
              }}
            >
              Cancel
            </Button>
            {step === 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isNameValid || !selectedIds.size}
                sx={{
                  textTransform: "none",
                  backgroundColor: COLOR_PRIMARY,
                  "&:hover": { backgroundColor: "#0d2c4d" }
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaving}
                sx={{
                  textTransform: "none",
                  backgroundColor: COLOR_PRIMARY,
                  "&:hover": { backgroundColor: "#0d2c4d" }
                }}
              >
                {isSaving ? "Saving..." : isEdit ? "Save Changes" : "Save Slice"}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

interface Step1Props {
  name: string;
  setName: (next: string) => void;
  nameError?: string | null;
  search: string;
  setSearch: (next: string) => void;
  investors: SlicePromptInvestor[];
  selectedIds: Set<SlicePromptInvestor["id"]>;
  toggleInvestor: (investor: SlicePromptInvestor) => void;
  loadError?: boolean;
  onRetryLoad?: () => void;
  selectedCount: number;
  listRef: React.RefObject<HTMLDivElement>;
}

const Step1: React.FC<Step1Props> = ({
  name,
  setName,
  nameError,
  search,
  setSearch,
  investors,
  selectedIds,
  toggleInvestor,
  loadError,
  onRetryLoad,
  selectedCount,
  listRef
}) => (
  <>
    <TextField
      label="Slice name"
      value={name}
      onChange={(e) => setName(e.target.value.slice(0, NAME_MAX_LENGTH))}
      autoFocus
      fullWidth
      error={Boolean(nameError)}
      inputProps={{ maxLength: NAME_MAX_LENGTH }}
      helperText={nameError ?? `${name.length}/${NAME_MAX_LENGTH}`}
    />

    <Stack
      direction="row"
      gap="8px"
      sx={{
        backgroundColor: COLOR_BG_INFO,
        border: `1px solid ${COLOR_INFO}33`,
        borderRadius: "6px",
        padding: "10px 12px"
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 18, color: COLOR_INFO, flexShrink: 0, mt: "1px" }} />
      <Typography sx={{ color: COLOR_PRIMARY, fontSize: "13px", lineHeight: "18px" }}>
        Slices with attached target policies are governed — changes are audited and create new policy versions.
      </Typography>
    </Stack>

    <Stack direction="row" alignItems="center" justifyContent="space-between" gap="12px">
      <Typography sx={{ color: COLOR_PRIMARY, fontSize: "14px", lineHeight: "20px", fontWeight: 500 }}>
        Members
      </Typography>
      <Typography sx={{ color: COLOR_SECONDARY, fontSize: "13px", lineHeight: "16px" }}>
        {selectedCount} selected
      </Typography>
    </Stack>

    <TextField
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      placeholder="Search by name"
      size="small"
      fullWidth
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 18, color: COLOR_SECONDARY }} />
          </InputAdornment>
        ),
        sx: { borderRadius: "8px" }
      }}
    />

    {loadError ? (
      <Stack
        sx={{
          padding: "16px",
          border: `1px solid ${COLOR_STROKE_DIVIDER}`,
          borderRadius: "6px",
          alignItems: "center",
          gap: "10px"
        }}
      >
        <Typography sx={{ color: COLOR_ERROR, fontSize: "14px" }}>Investment entities could not be loaded.</Typography>
        {onRetryLoad && (
          <Button variant="outlined" size="sm" onClick={onRetryLoad} sx={{ textTransform: "none" }}>
            Retry
          </Button>
        )}
      </Stack>
    ) : (
      <Stack
        ref={listRef}
        sx={{
          border: `1px solid ${COLOR_STROKE_DIVIDER}`,
          borderRadius: "6px",
          height: "300px",
          overflowY: "auto"
        }}
      >
        {investors.length === 0 ? (
          <Typography sx={{ p: "16px", color: COLOR_DISABLED, fontSize: "14px", textAlign: "center" }}>
            {search ? "No matches." : "No investors available."}
          </Typography>
        ) : (
          investors.map((investor) => {
            const checked = selectedIds.has(investor.id);
            return (
              <Stack
                key={investor.id}
                direction="row"
                alignItems="center"
                gap="12px"
                onClick={() => toggleInvestor(investor)}
                sx={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}`,
                  "&:last-of-type": { borderBottom: "none" },
                  "&:hover": { backgroundColor: COLOR_BG_HOVER_SUBTLE }
                }}
              >
                <Checkbox
                  checked={checked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleInvestor(investor)}
                  sx={{ padding: 0 }}
                />
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: COLOR_PRIMARY,
                      fontSize: "14px",
                      lineHeight: "20px",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {investor.name}
                  </Typography>
                </Stack>
              </Stack>
            );
          })
        )}
      </Stack>
    )}
  </>
);

interface Step2Props {
  name: string;
  selectedInvestors: SlicePromptInvestor[];
}

const Step2: React.FC<Step2Props> = ({ name, selectedInvestors }) => (
  <>
    <Stack gap="4px">
      <Typography sx={{ color: COLOR_SECONDARY, fontSize: "12px", lineHeight: "16px" }}>Slice name</Typography>
      <Typography sx={{ color: COLOR_PRIMARY, fontSize: "16px", lineHeight: "24px", fontWeight: 500 }}>
        {name}
      </Typography>
    </Stack>

    <Stack gap="4px">
      <Typography sx={{ color: COLOR_SECONDARY, fontSize: "12px", lineHeight: "16px" }}>
        Members ({selectedInvestors.length})
      </Typography>
      {selectedInvestors.length === 0 ? (
        <Typography sx={{ color: COLOR_DISABLED, fontSize: "14px", lineHeight: "20px" }}>
          No investment entities selected.
        </Typography>
      ) : (
        <Stack
          sx={{
            border: `1px solid ${COLOR_STROKE_DIVIDER}`,
            borderRadius: "6px",
            overflow: "hidden"
          }}
        >
          <Stack
            direction="row"
            sx={{
              backgroundColor: COLOR_BG_HOVER_SUBTLE,
              padding: "8px 12px",
              borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}`
            }}
          >
            <Typography sx={{ flex: 2, fontSize: "12px", fontWeight: 500, color: COLOR_PRIMARY }}>Name</Typography>
          </Stack>
          {selectedInvestors.map((inv) => (
            <Stack
              key={inv.id}
              direction="row"
              sx={{
                padding: "8px 12px",
                borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}`,
                "&:last-of-type": { borderBottom: "none" }
              }}
            >
              <Typography
                sx={{
                  flex: 2,
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: COLOR_PRIMARY,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {inv.name}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  </>
);
